import React, { useState, useEffect } from 'react'
import { FlagFormModalMetaProps, Flag, FlagCategory } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: FlagFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (flagData: any) => Promise<void>
    initialFlag?: Flag | null
    categories: FlagCategory[]
}

export const FlagFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialFlag, categories } = props

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category_id: null as number | null,
        has_expiry: false,
        hidden_from_registration: false
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Populate form when editing a flag
    useEffect(() => {
        if (initialFlag && isOpen) {
            setFormData({
                name: initialFlag.name,
                description: initialFlag.description || '',
                category_id: initialFlag.category_id || null,
                has_expiry: initialFlag.has_expiry || false,
                hidden_from_registration: initialFlag.hidden_from_registration || false
            })
        } else if (!isOpen) {
            // Reset form when modal closes
            setFormData({
                name: '',
                description: '',
                category_id: null,
                has_expiry: false,
                hidden_from_registration: false
            })
            setError(null)
        }
    }, [initialFlag, isOpen])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        if (name === 'category_id') {
            setFormData(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }))
        } else {
            setFormData(prev => ({ ...prev, [name]: value }))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="flag-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="flag-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                        {/* Flag Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.nameLabel} <span className="text-danger-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.namePlaceholder}
                            />
                        </div>

                        {/* Category */}
                        <div>
                            <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.categoryLabel}
                            </label>
                            <select
                                id="category_id"
                                name="category_id"
                                value={formData.category_id ?? ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="">{meta.noCategoryOption}</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.descriptionLabel}
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.descriptionPlaceholder}
                            />
                        </div>

                        {/* Has Expiry Flag */}
                        <div className='flex'>
                            <input
                                type="checkbox"
                                id='expiry'
                                checked={formData.has_expiry}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    has_expiry: e.target.checked
                                }))} className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 ms-2">
                                {meta.hasExpiryLabel}
                            </label>

                        </div>

                        {/* Hidden from Registration */}
                        <div className='flex'>
                            <input
                                type="checkbox"
                                id='hidden_from_registration'
                                checked={formData.hidden_from_registration}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    hidden_from_registration: e.target.checked
                                }))} className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <label htmlFor="hidden_from_registration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 ms-2">
                                {meta.hiddenFromRegistrationLabel}
                            </label>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
}
